/**
 * Client-side filters for unified טיסה+מלון SERP — same semantics as packages mockup.
 */

import {
  getDepartureWindow,
  getPackageSeatsLeft,
  MOCK_SEATS_FILTERS,
} from "../packagesResultsMockup/mockPackagesData";

/** Normalize Hubwayz / demo board codes into MOCK_BOARD_OPTIONS ids. */
export function normalizeBoardId(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (!s) return "ro";
  if (["ro", "bb", "hb", "fb"].includes(s)) return s;
  if (s === "ai" || s.includes("all inclusive") || s.includes("הכל כלול")) return "fb";
  if (
    s === "room only" ||
    s.includes("room only") ||
    s.includes("לינה בלבד") ||
    s === "only bed" ||
    s === "ob"
  ) {
    return "ro";
  }
  if (s.includes("breakfast") || s.includes("בוקר") || s === "bed and breakfast") {
    return "bb";
  }
  if (s.includes("half") || s.includes("חצי") || s.includes("demi")) return "hb";
  if (s.includes("full") || s.includes("מלא") || s.includes("pension")) return "fb";
  return s;
}

export function inferBaggageLevel(baggage) {
  if (!baggage || baggage.none) return "none";
  if (baggage.level) return String(baggage.level);
  const text = [...(baggage.bullets || []), baggage.badgeLabelHe || ""]
    .join(" ")
    .toLowerCase();
  if (!text.trim()) return "none";
  if (/ללא\s*כבודה|no\s*bag/.test(text)) return "none";
  if (/טרולי|trolley|cabin\s*bag|hand\s*bag/.test(text)) return "trolley";
  if (/מזוודה|checked|suitcase|luggage|כבודה/.test(text)) return "checked";
  return "checked";
}

function starBucket(stars) {
  const n = Number(stars) || 0;
  if (n >= 5) return 5;
  if (n >= 4) return 4;
  if (n >= 3) return 3;
  return 2;
}

function cardSortPrice(pkg) {
  const n = Number(pkg?.agentNetPerPerson ?? pkg?.pricePerPerson);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

/**
 * @param {object[]} cards
 * @param {object} filters
 */
export function filterUnifiedFlightHotelCards(cards, filters = {}) {
  const {
    hotelQuery = "",
    selectedBoards,
    selectedStars,
    selectedNights,
    agentQuick,
    baggageFilter,
    departWindow = "all",
    seatsFilter = "all",
    sortBy = "price-asc",
  } = filters;

  const hotelQ = String(hotelQuery || "").trim().toLowerCase();
  const boards = selectedBoards instanceof Set ? selectedBoards : new Set();
  const stars = selectedStars instanceof Set ? selectedStars : new Set();
  const nights = selectedNights instanceof Set ? selectedNights : new Set();
  const agent = agentQuick instanceof Set ? agentQuick : new Set();
  const bags = baggageFilter instanceof Set ? baggageFilter : new Set();

  let list = (cards || []).filter((pkg) => {
    if (boards.size) {
      const bid = normalizeBoardId(pkg.boardId || pkg.boardLabel);
      if (!boards.has(bid)) return false;
    }
    if (stars.size && !stars.has(starBucket(pkg.stars))) return false;
    if (nights.size) {
      const n = Number(pkg.nights);
      if (!Number.isFinite(n) || !nights.has(n)) return false;
    }
    if (hotelQ && !String(pkg.hotelName || "").toLowerCase().includes(hotelQ)) {
      return false;
    }
    if (agent.has("immediate") && !pkg.immediateConfirm) return false;
    if (agent.has("baggage") && inferBaggageLevel(pkg.baggage) === "none") return false;
    if (agent.has("direct") && !pkg.directFlight) return false;
    if (agent.has("transfer") && !pkg.transferIncluded) return false;

    if (bags.size) {
      const level = inferBaggageLevel(pkg.baggage);
      if (!bags.has(level)) return false;
    }
    if (departWindow !== "all") {
      if (getDepartureWindow(pkg.outbound?.depart) !== departWindow) return false;
    }
    if (seatsFilter !== "all") {
      const minSeats =
        MOCK_SEATS_FILTERS.find((f) => f.id === seatsFilter)?.minSeats ?? 0;
      const seatsLeft = getPackageSeatsLeft(pkg);
      if (seatsLeft == null || seatsLeft < minSeats) return false;
    }
    return true;
  });

  if (sortBy === "price-asc") {
    list = [...list].sort((a, b) => cardSortPrice(a) - cardSortPrice(b));
  } else if (sortBy === "price-desc") {
    list = [...list].sort((a, b) => cardSortPrice(b) - cardSortPrice(a));
  } else if (sortBy === "stars") {
    list = [...list].sort((a, b) => (Number(b.stars) || 0) - (Number(a.stars) || 0));
  }

  return list;
}
